/**
 * PriceTagEditor.jsx — inline editor for `price_coins` with the new
 * £-pound primary input + "No max" toggle (May 21 2026).
 *
 *   • Writer types pounds (e.g. 12.50) → converted to coins (round) at
 *     the canonical 3p/coin BUY rate before PATCH.
 *   • Default cap = £15. Toggle "No max" to bypass.
 *   • Server still enforces the cap server-side; client just mirrors.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Coins, Edit3, Check, X, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const COIN_TO_GBP = 0.03;          // 3p / coin BUY rate
const DEFAULT_MAX_GBP = 15;
const DEFAULT_MAX_COINS = Math.round(DEFAULT_MAX_GBP / COIN_TO_GBP);

const coinsToPounds = (c) => (c == null ? '' : (c * COIN_TO_GBP).toFixed(2));
const poundsToCoins = (p) => Math.round(Number(p) / COIN_TO_GBP);

const PriceTagEditor = ({
  contentId,
  value,           // current price in coins
  noMax = false,   // current `no_max_price` flag
  onSaved,         // (nextCoins, nextNoMax) → void
  size = 'md',
  testIdPrefix = 'price-tag',
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftPounds, setDraftPounds] = useState(coinsToPounds(value));
  const [draftNoMax, setDraftNoMax] = useState(!!noMax);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setDraftPounds(coinsToPounds(value));
    setDraftNoMax(!!noMax);
  }, [value, noMax]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const isFree = value == null || value === 0;
  const fontSize = size === 'sm' ? 'text-[10px]' : 'text-xs';
  const padding = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-1';

  const handleSave = async (override) => {
    setError('');
    let coins;
    let noMaxOut = draftNoMax;
    if (override === 'free') {
      coins = null;
    } else {
      const raw = draftPounds;
      if (raw === '' || raw === null) {
        coins = null;
      } else {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) { setError('Enter £0+'); return; }
        coins = poundsToCoins(n);
        if (!noMaxOut && coins > DEFAULT_MAX_COINS) {
          setError(`Max £${DEFAULT_MAX_GBP} (toggle No max to bypass)`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/content-pricing/${encodeURIComponent(contentId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ price_coins: coins, no_max_price: noMaxOut }),
        }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      onSaved?.(data.price_coins, !!data.no_max_price);
      setEditing(false);
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        className={`inline-flex items-center gap-1 rounded-full ${padding} ${fontSize} font-semibold border transition-colors ${
          isFree
            ? 'bg-slate-800/60 text-slate-400 border-slate-700 hover:border-slate-500'
            : 'bg-amber-500/15 text-amber-200 border-amber-500/40 hover:border-amber-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid={`${testIdPrefix}-${contentId}`}
        aria-label={isFree ? 'Set price' : `Edit price (currently £${coinsToPounds(value)})`}
      >
        {isFree ? (
          <span>Free</span>
        ) : (
          <>
            <span className="font-black">£{coinsToPounds(value)}</span>
            <span className="text-[9px] opacity-60 inline-flex items-center gap-0.5">
              · <Coins className="w-2.5 h-2.5" />{value}
            </span>
            {noMax && (
              <span className="text-[8px] uppercase tracking-wider px-1 rounded bg-rose-500/30 text-rose-200 border border-rose-400/40 ml-0.5">
                no max
              </span>
            )}
          </>
        )}
        {!disabled && <Edit3 className="w-2.5 h-2.5 opacity-60" />}
      </button>
    );
  }

  return (
    <div
      className="inline-flex flex-col gap-1 rounded-md p-2 bg-slate-900/80 border border-amber-400/60"
      data-testid={`${testIdPrefix}-${contentId}-editor`}
    >
      <div className="flex items-center gap-1">
        <span className="text-amber-300 font-bold text-xs">£</span>
        <input
          ref={inputRef}
          type="number"
          inputMode="decimal"
          min={0}
          max={draftNoMax ? 10000 : DEFAULT_MAX_GBP}
          step={0.5}
          value={draftPounds}
          onChange={(e) => setDraftPounds(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') { setEditing(false); setError(''); }
          }}
          placeholder="0.00"
          disabled={saving}
          className={`w-16 bg-transparent text-amber-100 ${fontSize} font-semibold tabular-nums focus:outline-none placeholder:text-slate-500`}
          data-testid={`${testIdPrefix}-${contentId}-input`}
        />
        <span className="text-[10px] text-slate-400 tabular-nums">
          ({draftPounds ? poundsToCoins(draftPounds) : 0} 🪙)
        </span>
        <button
          type="button"
          onClick={() => handleSave()}
          disabled={saving}
          className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 ml-1"
          data-testid={`${testIdPrefix}-${contentId}-save`}
          aria-label="Save price"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => handleSave('free')}
          disabled={saving}
          className="text-slate-400 hover:text-slate-200 text-[10px] font-bold px-1"
          data-testid={`${testIdPrefix}-${contentId}-free`}
          title="Mark as free"
        >
          FREE
        </button>
        <button
          type="button"
          onClick={() => { setEditing(false); setError(''); setDraftPounds(coinsToPounds(value)); setDraftNoMax(!!noMax); }}
          disabled={saving}
          className="text-slate-400 hover:text-rose-300"
          data-testid={`${testIdPrefix}-${contentId}-cancel`}
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* No-max toggle. When OFF, the £15 cap applies. */}
      <label className="flex items-center gap-2 text-[10px] text-slate-300 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={draftNoMax}
          onChange={(e) => setDraftNoMax(e.target.checked)}
          className="rounded border-slate-500 text-fuchsia-500 focus:ring-fuchsia-500/50"
          data-testid={`${testIdPrefix}-${contentId}-nomax`}
        />
        <span>
          No max <span className="text-slate-500">· bypass £{DEFAULT_MAX_GBP} cap</span>
        </span>
      </label>
      {error && (
        <span className="text-[10px] text-rose-300" data-testid={`${testIdPrefix}-${contentId}-error`}>
          {error}
        </span>
      )}
    </div>
  );
};

export default PriceTagEditor;
